<script setup lang="ts">
import type { ContentItem } from "@edco/apollo";
import type { ArticleArea } from "~/models/generated/ArticleArea";

const props = defineProps<{
  articleArea: ContentItem<ArticleArea>;
  heading?: string;
  text?: string;
  completedHeading?: string;
  completedText?: string;
  buttonLabel?: string;
}>();

const newsletterResource = $newsletterApiResource();

const submitted = ref( false );
const submitting = ref( false );
const email = ref<string>( "" );

const modalCompletedHeading = computed( () => {
  if ( props.completedHeading !== undefined ) {
    return props.completedHeading;
  }
  return props.articleArea.properties?.modalCompletedTitle || "Thanks";
} );

const hasModalButton = computed( () => {
  return !!props.articleArea.properties?.modalSubmissionButton?.[0]?.url;
} );

const modalCompletedText = computed( () => {
  if ( props.completedText !== undefined ) {
    return props.completedText;
  }
  return (
    props.articleArea.properties?.modalCompletedText
    || "We'll be in touch soon."
  );
} );

const modalHeading = computed( () => {
  if ( props.heading !== undefined ) {
    return props.heading;
  }
  return props.articleArea.properties?.modalTitle || "Find out more about joining the nea";
} );

const modalText = computed( () => {
  if ( props.text !== undefined ) {
    return props.text;
  }
  return (
    props.articleArea.properties?.modalText
    || `Express your interest in becoming a member of the nea by entering your email address below.\
    We'll contact you in the next few days to discuss next steps.`
  );
} );

const modalButtonText = computed( () => {
  if ( props.buttonLabel !== undefined ) {
    return props.buttonLabel;
  }
  if ( props.articleArea.properties?.modalSubmissionButton?.[0]?.title ) {
    return props.articleArea.properties.modalSubmissionButton[0].title;
  }
  if ( props.articleArea.properties.modalRegisterForNewsletterButtonLabel ) {
    return props.articleArea.properties.modalRegisterForNewsletterButtonLabel;
  }
  return "Express interest now";
} );

async function submit () {
  if (
    hasModalButton.value
    && props.articleArea.properties.modalSubmissionButton?.[0]
  ) {
    const { url, target }
      = props.articleArea.properties.modalSubmissionButton?.[0];
    if ( target === "_blank" && url ) {
      window.open( url );
    } else if ( url ) {
      window.location.href = url;
    }
  } else {
    try {
      submitting.value = true;
      if ( email.value ) {
        await newsletterResource.signUp( email.value );
        submitted.value = true;
      }
    } finally {
      submitting.value = false;
    }
  }
}
</script>

<template>
  <form
    v-if="!submitted"
    class="express-interest-form"
    @submit.prevent="submit">
    <h2>{{ modalHeading }}</h2>
    <p>{{ modalText }}</p>
    <div v-if="!hasModalButton">
      <AFormField
        v-slot="{ componentField }"
        v-model="email"
        name="email">
        <AFormItem>
          <AFormLabel>
            Your email address
          </AFormLabel>
          <AFormControl>
            <AInput
              type="text"
              v-bind="componentField"
              placeholder="Enter your email address"
              autocomplete="email"
              required />
          </AFormControl>
          <AFormMessage />
        </AFormItem>
      </AFormField>
    </div>

    <a-button
      id="signup__button"
      :disabled="submitting"
      variant="dark"
      type="submit"
      look="pill">
      {{ modalButtonText }}
    </a-button>
  </form>
  <div
    v-else
    class="express-interest-form">
    <h2>{{ modalCompletedHeading }}</h2>
    <p>{{ modalCompletedText }}</p>
  </div>
</template>

<style scoped lang="scss">
  .express-interest-form {
    padding: 30px;
    background: #eff8f7;
    border-radius: 20px;
  }
</style>
